import React from 'react';

import {registerOpenOverlay} from '../lib/openOverlays.js';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// What the trap probes on a candidate node, over and above what `Element`
// guarantees. `querySelectorAll` and `document.activeElement` are typed as
// Element, but this module duck-types three things the DOM does not put there:
// `focus` (HTMLElement only), `disabled` (form controls only), and the two
// offset dimensions (HTMLElement only). Declaring them as optional `unknown`
// describes the probe honestly — every one of them is guarded before use.
type FocusProbe = Element & {
  focus?: unknown;
  disabled?: unknown;
  offsetWidth?: unknown;
  offsetHeight?: unknown;
};

// A node this module has proved it can call `.focus()` on.
type FocusableElement = Element & {focus: () => void};

// "Can I call .focus() on this?" — nothing more. Kept separate from isFocusable
// because the RETURN-FOCUS target is deliberately not held to the visibility
// test: the opener may be scrolled out of view and must still get focus back.
function hasFocusMethod(node: FocusProbe | null | undefined): node is FocusableElement {
  return !!node && typeof node.focus === 'function';
}

function isFocusable(el: FocusProbe | null | undefined): el is FocusableElement {
  if (!el || typeof el.focus !== 'function') return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  if (el.disabled) return false;
  return !!(el.offsetWidth || el.offsetHeight || (el.getClientRects && el.getClientRects().length));
}

function focusInitial(dialog: HTMLElement | null, selector: string | null | undefined): void {
  if (!dialog) return;
  const initial = selector ? dialog.querySelector(selector) : null;
  const focusable = isFocusable(initial) ? initial : getFocusable(dialog)[0];
  if (focusable) focusable.focus();
  else if (typeof dialog.focus === 'function') dialog.focus();
}

function getFocusable(dialog: HTMLElement | null): FocusableElement[] {
  if (!dialog || typeof dialog.querySelectorAll !== 'function') return [];
  return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isFocusable);
}

export type ModalFocusTrapOptions = {
  onCancel: () => void;
  initialFocusSelector?: string;
};

// Parameterised by the dialog's own element type, defaulting to HTMLElement.
// React's `ref=` on an intrinsic element demands the EXACT element type
// (`RefObject<HTMLDivElement>` will not accept an `HTMLElement` ref), so a
// TypeScript caller names its root element once — `useModalFocusTrap<HTMLDivElement>(...)`
// — instead of asserting the ref at the JSX site. The default keeps every
// existing JavaScript caller unchanged, and nothing inside this module needs
// more than HTMLElement, which is what the `extends` clause guarantees.
export type ModalFocusTrap<T extends HTMLElement = HTMLElement> = {
  // Mutable rather than React.RefObject<T>: the caller assigns this to a `ref=`
  // on its dialog element, so React writes `.current` and it is null until the
  // first commit.
  dialogRef: React.MutableRefObject<T | null>;
  handleDialogKeyDown: (e: React.KeyboardEvent) => void;
};

export function useModalFocusTrap<T extends HTMLElement = HTMLElement>({
  onCancel,
  initialFocusSelector = '[data-modal-initial-focus]',
}: ModalFocusTrapOptions): ModalFocusTrap<T> {
  const dialogRef = React.useRef<T | null>(null);
  const returnFocusRef = React.useRef<FocusableElement | null>(null);

  // While open, count as an overlay so the phone bottom bar hides (Table C).
  React.useEffect(() => registerOpenOverlay(), []);

  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const active = document.activeElement;
    returnFocusRef.current = hasFocusMethod(active) ? active : null;

    const runFocus = () => focusInitial(dialogRef.current, initialFocusSelector);
    let rafId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      rafId = window.requestAnimationFrame(runFocus);
    } else {
      timeoutId = setTimeout(runFocus, 0);
    }

    return () => {
      if (rafId != null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
      const target = returnFocusRef.current;
      if (target && typeof target.focus === 'function') {
        try {
          target.focus();
        } catch (_e) {
          /* return-focus is best effort; the opener may have unmounted */
        }
      }
    };
  }, [initialFocusSelector]);

  const handleDialogKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = getFocusable(dialogRef.current);
      if (focusable.length === 0) {
        e.preventDefault();
        if (dialogRef.current && typeof dialogRef.current.focus === 'function') dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = typeof document !== 'undefined' ? document.activeElement : null;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
        return;
      }
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onCancel],
  );

  return {dialogRef, handleDialogKeyDown};
}
